/**
 * P2 链路测试台 fixture。
 *
 * Mock 模式也必须经过与真实页面相同的 API client 路径，避免按钮退化成纯前端假跑。
 * Fixture 需要随输入变化，不能把所有样本固定成某一个方案。
 */
import type {
	PipelineAdminRunEvent,
	PipelineAdminRunResponse,
	PipelineDispatchResponse,
	PipelineIntentResponse,
	PipelineMessageResponse,
} from "@/lib/api/pipeline";

import { registerFixture } from "./registry";

function l1Intent(
	intent: string,
	serviceStage: string,
	subTask: string | null,
	confidence: number,
	missingInfo: string[] = [],
): PipelineIntentResponse {
	return {
		intent,
		service_stage: serviceStage,
		dialogue_signal: { sentiment: "neutral", intent_shift: "none" },
		intent_continuity: "switch",
		confidence,
		topic: `${intent}.${subTask ?? "none"}`,
		secondary_intent: null,
		sub_task: subTask,
		dialogue_state: missingInfo.length > 0 ? "clarify_needed" : "actionable",
		missing_info: missingInfo,
		inferred_focused_product: null,
		inferred_focused_order: null,
		reasoning: "mock_p11_l1_business_semantics",
		implementation_level: "L1",
		l2_matched_rule_id: null,
	};
}

function l2Intent(
	intent: string,
	serviceStage: string,
	ruleId: string,
	confidence: number,
): PipelineIntentResponse {
	return {
		intent,
		service_stage: serviceStage,
		dialogue_signal: { sentiment: "neutral", intent_shift: "none" },
		intent_continuity: "switch",
		confidence,
		topic: `${intent}.none`,
		secondary_intent: null,
		sub_task: null,
		dialogue_state: "actionable",
		missing_info: [],
		inferred_focused_product: null,
		inferred_focused_order: null,
		reasoning: `mock_p11_l2_fast_path_${ruleId}`,
		implementation_level: "L2",
		l2_matched_rule_id: ruleId,
	};
}

const ADDRESS_CHANGE_INTENT = l1Intent("address_change", "in_sale", "缺字段补全", 0.86, [
	"focused_order",
	"new_address",
]);

const UNKNOWN_INTENT: PipelineIntentResponse = {
	intent: "unknown",
	service_stage: "unknown",
	dialogue_signal: { sentiment: "neutral", intent_shift: "none" },
	intent_continuity: "switch",
	confidence: 0,
	topic: "mock_unknown_no_rule",
	secondary_intent: null,
	sub_task: null,
	dialogue_state: "closed",
	missing_info: [],
	inferred_focused_product: null,
	inferred_focused_order: null,
	reasoning: "mock_unknown_no_business_intent",
	implementation_level: "L1",
	l2_matched_rule_id: null,
};

const GREETING_INTENT = l2Intent("unknown", "unknown", "R004", 0.95);

const REFUND_INTENT = l1Intent("refund_request", "after_sale", "主观退", 0.91, ["focused_order"]);

const REFUND_STATUS_INTENT = l1Intent("refund_request", "after_sale", "状态查询", 0.9);

const RETURN_INTENT = l1Intent("return_request", "after_sale", "标准退", 0.88, ["focused_order"]);

const RETURN_STATUS_INTENT = l1Intent("return_request", "after_sale", "状态查询", 0.9);

const CANCEL_INTENT = l1Intent("order_cancel", "in_sale", "未发货取消", 0.87, ["focused_order"]);

const CANCEL_FAST_PATH_INTENT = l2Intent("order_cancel", "in_sale", "R002", 0.96);

const PRODUCT_INTENT = l1Intent("product_inquiry", "pre_sale", "商品事实", 0.9);

const PRODUCT_RECOMMENDATION_INTENT = l1Intent(
	"product_recommendation",
	"pre_sale",
	"新需求",
	0.86,
);

const DISCOUNT_INTENT = l1Intent("discount_inquiry", "pre_sale", "优惠码有效", 0.88);

const SHIPPING_INTENT = l1Intent("shipping_tracking", "after_sale", "在途", 0.84, [
	"focused_order",
]);

const ORDER_STATUS_FAST_PATH_INTENT = l2Intent("order_status_inquiry", "in_sale", "R003", 0.96);

const ORDER_ISSUE_INTENT = l1Intent("order_issue", "after_sale", "已损坏", 0.9, ["focused_order"]);

const HUMAN_HANDOFF_INTENT = l2Intent("human_handoff", "unknown", "R001", 0.96);

function requestMessage(body: unknown): string {
	if (typeof body === "string") {
		try {
			const parsed = JSON.parse(body) as { message?: unknown };
			return typeof parsed.message === "string" ? parsed.message : "";
		} catch {
			return "";
		}
	}
	if (body && typeof body === "object" && "message" in body) {
		const message = (body as { message?: unknown }).message;
		return typeof message === "string" ? message : "";
	}
	return "";
}

function requestBodyRecord(body: unknown): Record<string, unknown> {
	if (typeof body === "string") {
		try {
			const parsed = JSON.parse(body) as unknown;
			return parsed && typeof parsed === "object" ? (parsed as Record<string, unknown>) : {};
		} catch {
			return {};
		}
	}
	return body && typeof body === "object" ? (body as Record<string, unknown>) : {};
}

function requestHistoryCount(body: unknown): number {
	const record = requestBodyRecord(body);
	return Array.isArray(record.history) ? record.history.length : 0;
}

function requestDataMode(body: unknown): string | null {
	const mode = requestBodyRecord(body).data_mode;
	return typeof mode === "string" ? mode : null;
}

function intentForMessage(message: string): PipelineIntentResponse {
	const normalized = message.trim().toLowerCase();
	if (!normalized) return UNKNOWN_INTENT;
	const hasOrderReference = /(#\d{3,}|\border\s*#?\d{3,}\b|订单号\s*\d{3,}|订单\s*#?\d{3,})/i.test(
		normalized,
	);
	if (
		/^(你好|您好|谢谢|感谢|再见|hello|hi|hey|thanks|thank you|bye)[!.。！\s]*$/i.test(normalized)
	) {
		return GREETING_INTENT;
	}
	if (
		/(转人工|真人|找人|换人|人工客服|talk to (a )?human|real person|human agent|live agent)/i.test(
			normalized,
		)
	) {
		return HUMAN_HANDOFF_INTENT;
	}
	if (
		hasOrderReference &&
		/(订单状态|订单进度|查订单|状态|到哪了|发货了吗|order status|where is|shipped yet)/i.test(
			normalized,
		)
	) {
		return ORDER_STATUS_FAST_PATH_INTENT;
	}
	if (
		/(碎|破损|损坏|坏了|错件|缺件|丢了|丢失|少了一件|damaged|broken|wrong item|missing item|lost package)/i.test(
			normalized,
		)
	) {
		return ORDER_ISSUE_INTENT;
	}
	if (
		/(改地址|换地址|收货地址|修改地址|更改地址|地址|change address|shipping address|wrong address|address)/i.test(
			normalized,
		)
	) {
		return ADDRESS_CHANGE_INTENT;
	}
	if (/(取消订单|撤单|取消|cancel order|cancel)/i.test(normalized)) {
		return hasOrderReference ? CANCEL_FAST_PATH_INTENT : CANCEL_INTENT;
	}
	if (/(退货|退回|寄回|return|send back)/i.test(normalized)) {
		if (/(状态|进度|到哪|处理到哪|return status|return progress)/i.test(normalized)) {
			return RETURN_STATUS_INTENT;
		}
		return RETURN_INTENT;
	}
	if (/(退款|退钱|申请退款|refund|money back|chargeback)/i.test(normalized)) {
		if (/(状态|进度|到哪|处理到哪|refund status|refund progress)/i.test(normalized)) {
			return REFUND_STATUS_INTENT;
		}
		return REFUND_INTENT;
	}
	if (/(优惠码|优惠券|折扣|coupon|discount|promo|spring10)/i.test(normalized)) {
		return DISCOUNT_INTENT;
	}
	if (/(推荐|选哪个|怎么选|对比|比较|recommend|which one|compare)/i.test(normalized)) {
		return PRODUCT_RECOMMENDATION_INTENT;
	}
	if (
		/(你们卖什么|卖啥|卖什么|店铺.*卖|有什么商品|有什么产品|商品|产品|礼品卡|滑雪板|推荐|product|catalog|snowboard|gift card)/i.test(
			normalized,
		)
	) {
		return PRODUCT_INTENT;
	}
	if (/(物流|快递|发货|到货|tracking|shipping|shipped|package|订单|order)/i.test(normalized)) {
		return SHIPPING_INTENT;
	}
	return UNKNOWN_INTENT;
}

function dispatchForIntent(intent: PipelineIntentResponse): PipelineDispatchResponse {
	if (intent.intent === "order_status_inquiry") {
		return {
			intent,
			recommended_playbook: {
				id: "PB01_order_query",
				name: "订单与物流查询方案",
				guidance: "Mock guidance",
				fallback_hint: "缺订单信息时追问",
				version: "V0.1",
			},
			recommended_skills: ["SK01", "SK02"],
			universal_skills: ["SK05", "SK11"],
			match_mode: "config_mapping",
		};
	}
	if (intent.intent === "address_change") {
		return {
			intent,
			recommended_playbook: {
				id: "PB05_address_change",
				name: "地址修改方案",
				guidance: "Mock guidance",
				fallback_hint: "缺订单号时追问",
				version: "V0.1",
			},
			recommended_skills: ["SK01", "SK10"],
			universal_skills: ["SK05", "SK11"],
			match_mode: "config_mapping",
		};
	}
	if (intent.intent === "refund_request") {
		if (intent.sub_task === "状态查询") {
			return {
				intent,
				recommended_playbook: null,
				recommended_skills: ["SK02"],
				universal_skills: ["SK05", "SK11"],
				match_mode: "short_circuit",
				short_circuit_skill: "SK02",
				routing_rule_id: "rt_refund_status_short_circuit",
			};
		}
		return {
			intent,
			recommended_playbook: {
				id: "PB02_refund",
				name: "退款办理方案",
				guidance: "Mock guidance",
				fallback_hint: "缺订单信息时追问",
				version: "V0.1",
			},
			recommended_skills: ["SK01", "SK02", "SK09"],
			universal_skills: ["SK05", "SK11"],
			match_mode: "config_mapping",
		};
	}
	if (intent.intent === "return_request") {
		if (intent.sub_task === "状态查询") {
			return {
				intent,
				recommended_playbook: null,
				recommended_skills: ["SK02"],
				universal_skills: ["SK05", "SK11"],
				match_mode: "short_circuit",
				short_circuit_skill: "SK02",
				routing_rule_id: "rt_return_status_short_circuit",
			};
		}
		return {
			intent,
			recommended_playbook: {
				id: "PB03_return",
				name: "退货办理方案",
				guidance: "Mock guidance",
				fallback_hint: "缺订单信息时追问退货原因和订单号",
				version: "V0.1",
			},
			recommended_skills: ["SK01", "SK02", "SK08"],
			universal_skills: ["SK05", "SK11"],
			match_mode: "config_mapping",
		};
	}
	if (intent.intent === "order_issue") {
		return {
			intent,
			recommended_playbook: {
				id: "PB03_return",
				name: "退货办理方案",
				guidance: "Mock guidance",
				fallback_hint: "先追问订单信息和异常类型，branch 细分后续再做",
				version: "V0.1",
			},
			recommended_skills: ["SK01", "SK02", "SK08"],
			universal_skills: ["SK05", "SK11"],
			match_mode: "routing_table",
			routing_rule_id: "rt_order_issue_default",
		};
	}
	if (intent.intent === "order_cancel") {
		return {
			intent,
			recommended_playbook: {
				id: "PB04_cancel",
				name: "取消订单方案",
				guidance: "Mock guidance",
				fallback_hint: "先确认订单状态，已发货转退货方案",
				version: "V0.1",
			},
			recommended_skills: ["SK01", "SK02", "SK07"],
			universal_skills: ["SK05", "SK11"],
			match_mode: "config_mapping",
		};
	}
	if (intent.intent === "product_inquiry") {
		return {
			intent,
			recommended_playbook: {
				id: "PB06_pre_sale",
				name: "售前商品方案",
				guidance: "Mock guidance",
				fallback_hint: "商品事实优先来自 Shopify 商品目录，知识与证据只做补充说明",
				version: "V0.1",
			},
			recommended_skills: ["SK03"],
			universal_skills: ["SK05", "SK11"],
			match_mode: "config_mapping",
		};
	}
	if (intent.intent === "product_recommendation") {
		return {
			intent,
			recommended_playbook: {
				id: "PB06_pre_sale",
				name: "售前商品方案",
				guidance: "Mock guidance",
				fallback_hint: "推荐和对比先查商品事实，缺偏好时追问预算、用途或尺码",
				version: "V0.1",
			},
			recommended_skills: ["SK03", "SK04"],
			universal_skills: ["SK05", "SK11"],
			match_mode: "config_mapping",
		};
	}
	if (intent.intent === "discount_inquiry") {
		return {
			intent,
			recommended_playbook: {
				id: "PB07_discount",
				name: "优惠咨询方案",
				guidance: "Mock guidance",
				fallback_hint: "未返回优惠事实时说明需要人工或后续接入 SK06",
				version: "V0.1",
			},
			recommended_skills: ["SK06"],
			universal_skills: ["SK05", "SK11"],
			match_mode: "config_mapping",
		};
	}
	if (intent.intent === "shipping_tracking") {
		return {
			intent,
			recommended_playbook: {
				id: "PB01_order_query",
				name: "订单与物流查询方案",
				guidance: "Mock guidance",
				fallback_hint: "缺订单信息时追问",
				version: "V0.1",
			},
			recommended_skills: ["SK01", "SK03"],
			universal_skills: ["SK05", "SK11"],
			match_mode: "config_mapping",
		};
	}
	if (intent.intent === "human_handoff") {
		return {
			intent,
			recommended_playbook: null,
			recommended_skills: ["SK11"],
			universal_skills: ["SK05", "SK11"],
			match_mode: "no_match",
		};
	}
	return {
		intent,
		recommended_playbook: null,
		recommended_skills: [],
		universal_skills: ["SK05", "SK11"],
		match_mode: "no_match",
	};
}

function semanticFrameForIntent(
	intent: PipelineIntentResponse,
	message: string,
): Record<string, unknown> {
	if (intent.intent === "unknown") {
		const isGreeting = /^(你好|您好|hello|hi|hey)[!.。！\s]*$/i.test(message.trim());
		return {
			frame_version: "semantic_frame_v1",
			core_intent: "unknown",
			service_stage: "unknown",
			semantic_task: isGreeting ? "greeting" : "general_clarification",
			user_goal: "用户当前需求尚未明确",
			entities: {},
			preferences: {},
			constraints: {},
			context_signals: { source: "p2_fixture", message },
			missing_slots: [],
			evidence_needs: [],
			risk_flags: [],
			confidence: intent.confidence,
			semantic_reason: isGreeting
				? "问候语应由主 Agent 友好承接，不应固定误导为地址变更或直接转人工。"
				: "mock 模式下保留 unknown，不固定误导为地址变更方案。",
		};
	}
	if (intent.intent === "refund_request") {
		return {
			frame_version: "semantic_frame_v1",
			core_intent: "refund_request",
			service_stage: "after_sale",
			semantic_task: "refund_or_return_request",
			user_goal: "确认退款或退货处理方式",
			entities: { order_id: message.match(/\d+/)?.[0] ?? null },
			preferences: {},
			constraints: {},
			context_signals: { source: "p2_fixture", message },
			missing_slots: message.match(/\d+/) ? [] : ["order_id_or_customer_email"],
			evidence_needs: ["shopify_order", "policy_kb"],
			risk_flags: ["money_related"],
			confidence: intent.confidence,
			semantic_reason: "退款问题需要订单事实和政策依据共同支持。",
		};
	}
	if (intent.intent === "product_inquiry") {
		return {
			frame_version: "semantic_frame_v1",
			core_intent: "product_inquiry",
			service_stage: "pre_sale",
			semantic_task: "list_products",
			user_goal: "了解店铺销售的商品范围",
			entities: { list_products: true },
			preferences: {},
			constraints: {},
			context_signals: { source: "p2_fixture", message },
			missing_slots: [],
			evidence_needs: ["shopify_products", "product_kb"],
			risk_flags: [],
			confidence: intent.confidence,
			semantic_reason: "商品泛问法应准备商品目录事实，再由主 Agent 生成客户可见回答。",
		};
	}
	if (intent.intent === "shipping_tracking") {
		return {
			frame_version: "semantic_frame_v1",
			core_intent: "shipping_tracking",
			service_stage: "after_sale",
			semantic_task: "order_lookup",
			user_goal: "查询订单或物流状态",
			entities: { product_name: /snowboard/i.test(message) ? "snowboard" : null },
			preferences: {},
			constraints: {},
			context_signals: { source: "p2_fixture", message },
			missing_slots: ["order_id_or_customer_email"],
			evidence_needs: ["shopify_order", "policy_kb"],
			risk_flags: [],
			confidence: intent.confidence,
			semantic_reason: "物流查询需要订单定位信息；缺失时应追问而不是编造。",
		};
	}
	if (intent.intent === "order_status_inquiry") {
		return {
			frame_version: "semantic_frame_v1",
			core_intent: "order_status_inquiry",
			service_stage: "in_sale",
			semantic_task: "order_status_lookup",
			user_goal: "查询指定订单状态",
			entities: { order_id: message.match(/\d+/)?.[0] ?? null },
			preferences: {},
			constraints: {},
			context_signals: { source: "p2_fixture", message },
			missing_slots: message.match(/\d+/) ? [] : ["order_id_or_customer_email"],
			evidence_needs: ["shopify_order", "policy_kb"],
			risk_flags: [],
			confidence: intent.confidence,
			semantic_reason: "带明确订单号的状态查询可走 L2 快路径，但仍需要订单事实支撑回复。",
		};
	}
	if (intent.intent === "order_issue") {
		return {
			frame_version: "semantic_frame_v1",
			core_intent: "order_issue",
			service_stage: "after_sale",
			semantic_task: "received_order_issue",
			user_goal: "反馈到货商品或包裹异常",
			entities: { issue_type: "damaged_item", order_id: message.match(/\d+/)?.[0] ?? null },
			preferences: {},
			constraints: {},
			context_signals: { source: "p2_fixture", message },
			missing_slots: ["focused_order"],
			evidence_needs: ["shopify_order", "policy_kb"],
			risk_flags: ["after_sale_issue"],
			confidence: intent.confidence,
			semantic_reason: "订单异常由 RoutingTable 先进入 PB03，后续需要追问订单信息和异常细节。",
		};
	}
	if (intent.intent === "human_handoff") {
		return {
			frame_version: "semantic_frame_v1",
			core_intent: "human_handoff",
			service_stage: "unknown",
			semantic_task: "handoff_request",
			user_goal: "转人工处理",
			entities: {},
			preferences: {},
			constraints: {},
			context_signals: { source: "p2_fixture", message },
			missing_slots: [],
			evidence_needs: [],
			risk_flags: ["handoff_requested"],
			confidence: intent.confidence,
			semantic_reason: "明确转人工请求属于 L2 快路径。",
		};
	}
	return {
		frame_version: "semantic_frame_v1",
		core_intent: intent.intent,
		service_stage: intent.service_stage,
		semantic_task: "address_change_request",
		user_goal: "修改收货地址或确认是否仍可改地址",
		entities: { action: "change_address" },
		preferences: {},
		constraints: {},
		context_signals: { source: "p2_fixture", message },
		missing_slots: ["order_id_or_customer_email"],
		evidence_needs: ["shopify_order", "policy_kb"],
		risk_flags: ["potential_side_effect"],
		confidence: intent.confidence,
		semantic_reason: "地址修改需要先确认订单状态，再判断是否允许执行写操作。",
	};
}

function messageResponse(message: string): PipelineMessageResponse {
	const intent = intentForMessage(message);
	const dispatch = dispatchForIntent(intent);
	const isUnknown = intent.intent === "unknown";
	const playbookId = dispatch.recommended_playbook?.id ?? null;
	const semanticFrame = semanticFrameForIntent(intent, message);
	const missingSlots = Array.isArray(semanticFrame.missing_slots)
		? semanticFrame.missing_slots.map((item) => String(item)).filter(Boolean)
		: [];
	const hasEvidence = !isUnknown && missingSlots.length === 0;
	const notTriggered =
		!isUnknown && missingSlots.length > 0
			? dispatch.recommended_skills
					.filter((skillId) => ["SK01", "SK02"].includes(skillId))
					.map((skillId) => ({
						skill_id: skillId,
						reason: "slot_missing_before_request",
						missing_slots: missingSlots,
						registry_state: "enabled",
						playbook_allowed: true,
						pre_fetchable: true,
						source: "phase2_prefetch_plan",
					}))
			: [];
	const routedEvidenceRef = dispatch.short_circuit_skill
		? `skill.${dispatch.short_circuit_skill}`
		: `playbook.${playbookId ?? "unknown"}`;
	const evidenceRefs = hasEvidence ? ["policy.shipping_status", routedEvidenceRef] : [];
	const allowedSkills = [...dispatch.recommended_skills, ...dispatch.universal_skills];
	const actionType = hasEvidence ? "inform" : "clarify";
	const finalOutcome = hasEvidence ? "success" : "partial";
	const runtimeCapabilities = {
		phase4_runner_mode: "static",
		prompt_effect_mode: "rendered_not_consumed",
		rag_mode: "null",
		session_mode: "pg_session",
		quality_mode: "q_fast_grounding_starter",
		handoff_mode: "contract_only",
		shopify_read_mode: hasEvidence ? "live_read" : "not_requested",
		shopify_write_mode: "disabled",
	} as const;
	const replyText = isUnknown
		? "您好，我在的。您可以直接告诉我想咨询商品、订单、物流还是售后问题。"
		: hasEvidence
			? intent.intent === "product_inquiry"
				? "我们店铺主要销售滑雪板和礼品卡。您想看具体商品列表、价格，还是想按用途挑一款？"
				: "我查到了可用事实，会按订单状态和政策给出处理建议。"
			: "我可以帮你继续处理。请先提供订单号或下单邮箱，这样我才能查询订单状态并判断下一步。";
	return {
		trace_id: isUnknown ? "trace-demo-p2-unknown" : "trace-demo-p2-001",
		session_id: isUnknown ? "session-demo-p2-unknown" : "session-demo-p2-001",
		intent,
		recommended_playbook: dispatch.recommended_playbook,
		recommended_skills: dispatch.recommended_skills,
		universal_skills: dispatch.universal_skills,
		match_mode: dispatch.match_mode,
		selected_branch: dispatch.selected_branch ?? null,
		selected_branch_display_name: null,
		selected_branch_source: null,
		short_circuit_skill: dispatch.short_circuit_skill ?? null,
		routing_rule_id: dispatch.routing_rule_id ?? null,
		reply_text: replyText,
		runtime_capabilities: runtimeCapabilities,
		reply_placeholder: replyText,
		history_turns_included: 1,
		final_action_type: actionType,
		final_outcome: finalOutcome,
		phase_trace: [
			{
				phase: "phase1_refine",
				status: "ok",
				elapsed_ms: 1,
				summary: {
					safety_blocked: false,
					safety_reasons: [],
					slot_keys: isUnknown ? [] : ["product_name", "action"],
					message,
				},
			},
			{
				phase: "phase2_recall",
				status: isUnknown ? "no_match" : "ok",
				elapsed_ms: 8,
				summary: {
					playbook_id: playbookId,
					match_mode: dispatch.match_mode,
					selected_branch: dispatch.selected_branch ?? null,
					short_circuit_skill: dispatch.short_circuit_skill ?? null,
					routing_rule_id: dispatch.routing_rule_id ?? null,
					allowed_skills: allowedSkills,
					skill_result_count: hasEvidence ? 1 : 0,
					not_triggered_count: notTriggered.length,
					not_triggered: notTriggered,
					ambiguity_options_count: 0,
					ambiguity_options: [],
					rag_snippet_count: hasEvidence ? 1 : 0,
					rag_mode: runtimeCapabilities.rag_mode,
					semantic_task: semanticFrame.semantic_task,
					evidence_needs: semanticFrame.evidence_needs,
					missing_slots: semanticFrame.missing_slots,
					semantic_frame: semanticFrame,
				},
			},
			{
				phase: "phase3_rerank",
				status: "ok",
				elapsed_ms: 1,
				summary: {
					skill_result_count: hasEvidence ? 1 : 0,
					rag_snippet_count: hasEvidence ? 1 : 0,
				},
			},
			{
				phase: "phase4_generate",
				status: "natural",
				elapsed_ms: 1,
				summary: {
					action_type: actionType,
					evidence_refs: evidenceRefs,
					executed_action_count: 0,
					runner_mode: runtimeCapabilities.phase4_runner_mode,
					prompt_effect_mode: runtimeCapabilities.prompt_effect_mode,
					semantic_task: semanticFrame.semantic_task,
					missing_slots: missingSlots,
					evidence_status: hasEvidence ? "has_evidence" : "no_evidence_needed_or_available",
					decision_basis:
						missingSlots.length > 0
							? "missing_required_information"
							: hasEvidence
								? "evidence_grounded"
								: "general_clarification",
					customer_visible_action: hasEvidence ? "基于证据回答" : "追问补充信息",
					prompt_snapshot: {
						prompt_version: "m05-v1",
						runner_mode: runtimeCapabilities.phase4_runner_mode,
						prompt_effect_mode: runtimeCapabilities.prompt_effect_mode,
						rendered_prompt_tokens: 160,
						injected_variables: {
							current_user_message: message,
							semantic_context: `semantic_task=${semanticFrame.semantic_task}; missing_slots=${missingSlots.join(",")}`,
							playbook: {
								id: playbookId,
								name: dispatch.recommended_playbook?.name ?? null,
								guidance: dispatch.recommended_playbook?.guidance ?? "",
								fallback_hint: dispatch.recommended_playbook?.fallback_hint ?? "",
							},
							allowed_skills: allowedSkills,
							step_budget: 4,
							tool_runtime_context: {
								auto_invoked_skill_ids: hasEvidence ? [dispatch.short_circuit_skill ?? "SK03"] : [],
								rag_snippet_count: hasEvidence ? 1 : 0,
								evidence_refs: evidenceRefs,
							},
							d_prime_context: hasEvidence
								? "<d_prime>已预取商品、政策和方案证据。</d_prime>"
								: "<d_prime>本轮没有预取证据。</d_prime>",
						},
						rendered_prompt: "你是主 Agent，请根据注入变量、证据和服务边界生成本轮回复。",
						model_output: {
							reply: { main: replyText },
							action_type: actionType,
							evidence_refs: evidenceRefs,
							progression_state: hasEvidence ? "closing" : "need_gathering",
						},
						final_reply: replyText,
					},
				},
			},
			{
				phase: "phase5_validate",
				status: "pass",
				elapsed_ms: 1,
				summary: {
					decision: "PASS",
					block_reason: null,
					quality_mode: runtimeCapabilities.quality_mode,
				},
			},
		],
	};
}

function event(
	response: PipelineMessageResponse,
	index: number,
	eventType: PipelineAdminRunEvent["event_type"],
	stageId: PipelineAdminRunEvent["stage_id"],
	status: PipelineAdminRunEvent["status"],
	title: string,
	summary: string,
	extra?: Partial<PipelineAdminRunEvent>,
): PipelineAdminRunEvent {
	const runId = `run_${response.trace_id}`;
	return {
		run_id: runId,
		trace_id: response.trace_id,
		session_id: response.session_id,
		event_id: `${runId}:${String(index).padStart(3, "0")}`,
		event_type: eventType,
		stage_id: stageId,
		status,
		title,
		summary,
		input: {},
		decision: {},
		output: {},
		evidence_refs: [],
		field_refs: [],
		raw: {},
		...extra,
	};
}

function adminRunResponse(message: string, body: unknown): PipelineAdminRunResponse {
	const response = messageResponse(message);
	const phase2 = response.phase_trace.find((entry) => entry.phase === "phase2_recall");
	const phase4 = response.phase_trace.find((entry) => entry.phase === "phase4_generate");
	const phase5 = response.phase_trace.find((entry) => entry.phase === "phase5_validate");
	const runId = `run_${response.trace_id}`;
	const semanticTask = String(phase2?.summary.semantic_task ?? "未返回");
	const skillCount = Number(phase2?.summary.skill_result_count ?? 0);
	const notTriggered = Array.isArray(phase2?.summary.not_triggered)
		? phase2.summary.not_triggered
		: [];
	const ragCount = Number(phase2?.summary.rag_snippet_count ?? 0);
	const events: PipelineAdminRunEvent[] = [
		event(
			response,
			1,
			"run_started",
			"input_context",
			"success",
			"开始运行",
			"已创建 Admin run，开始按主链路处理本轮客户消息。",
			{
				input: {
					merchant_id: "mer_nY0bZmLI",
					data_mode: requestDataMode(body),
					history_count: requestHistoryCount(body),
				},
				field_refs: ["AdminRunRequest.data_mode", "AdminRunRequest.history"],
			},
		),
		event(response, 2, "message_received", "input_context", "success", "收到用户问题", message, {
			input: { message },
			field_refs: ["MessagesRequest.message"],
		}),
		event(
			response,
			3,
			"context_loaded",
			"input_context",
			"success",
			"已读取上下文",
			`请求传入 ${requestHistoryCount(body)} 条 history，返回 session=${response.session_id}。`,
			{
				input: { request_history_count: requestHistoryCount(body) },
				output: {
					session_id: response.session_id,
					session_mode: response.runtime_capabilities.session_mode,
				},
				field_refs: ["MessagesRequest.history", "MessagesResponse.session_id"],
			},
		),
		event(
			response,
			4,
			"intent_completed",
			"understanding_plan",
			response.intent.intent === "unknown" ? "warning" : "success",
			"已完成意图分类",
			`意图分类为 ${response.intent.intent}，置信度 ${response.intent.confidence.toFixed(2)}。`,
			{
				decision: response.intent as unknown as Record<string, unknown>,
				field_refs: ["MessagesResponse.intent"],
			},
		),
		event(
			response,
			5,
			"semantic_frame_completed",
			"understanding_plan",
			semanticTask === "未返回" ? "warning" : "success",
			"已形成客户目标理解",
			`语义任务 ${semanticTask}。`,
			{
				decision: {
					semantic_task: semanticTask,
					semantic_frame: phase2?.summary.semantic_frame,
				},
				field_refs: ["MessagesResponse.phase_trace.phase2_recall.summary.semantic_frame"],
				raw: phase2 ? { phase2 } : {},
			},
		),
		event(
			response,
			6,
			"playbook_recalled",
			"understanding_plan",
			response.recommended_playbook || response.short_circuit_skill ? "success" : "warning",
			"已检查处理路径",
			response.short_circuit_skill
				? `命中短路径 ${response.short_circuit_skill}。`
				: response.recommended_playbook
					? `命中方案 ${response.recommended_playbook.id}。`
					: "未命中处理路径，主 Agent 仍可基于证据继续回答或追问。",
			{
				decision: {
					playbook_id: response.recommended_playbook?.id ?? null,
					match_mode: response.match_mode,
					short_circuit_skill: response.short_circuit_skill,
					routing_rule_id: response.routing_rule_id,
				},
				output: {
					recommended_skills: response.recommended_skills,
					universal_skills: response.universal_skills,
					short_circuit_skill: response.short_circuit_skill,
				},
				field_refs: [
					"MessagesResponse.recommended_playbook",
					"MessagesResponse.match_mode",
					"MessagesResponse.short_circuit_skill",
					"MessagesResponse.routing_rule_id",
				],
			},
		),
		event(
			response,
			7,
			"evidence_fetch_started",
			"evidence",
			"success",
			"开始准备事实证据",
			`需要证据：${stringifyFixtureValue(phase2?.summary.evidence_needs)}`,
			{ decision: { evidence_needs: phase2?.summary.evidence_needs ?? [] } },
		),
		event(
			response,
			8,
			"tool_completed",
			"evidence",
			skillCount > 0 ? "success" : "skipped",
			"工具调用完成",
			`工具事实 ${skillCount} 条。`,
			{
				output: {
					skill_result_count: skillCount,
					not_triggered_count: notTriggered.length,
					not_triggered: notTriggered,
				},
				field_refs: ["MessagesResponse.phase_trace.phase2_recall.summary.skill_result_count"],
			},
		),
		event(
			response,
			9,
			"rag_completed",
			"evidence",
			ragCount > 0 ? "success" : "skipped",
			"知识召回完成",
			`知识片段 ${ragCount} 条。`,
			{
				output: { rag_snippet_count: ragCount, rag_mode: response.runtime_capabilities.rag_mode },
				field_refs: ["MessagesResponse.phase_trace.phase2_recall.summary.rag_snippet_count"],
			},
		),
		event(
			response,
			10,
			"generation_completed",
			"reply_quality",
			"success",
			"生成回复完成",
			`最终动作 ${response.final_action_type}。`,
			{
				output: { reply_text: response.reply_text, action_type: phase4?.summary.action_type },
				evidence_refs: Array.isArray(phase4?.summary.evidence_refs)
					? phase4.summary.evidence_refs.map((item) => String(item))
					: [],
				field_refs: ["MessagesResponse.reply_text", "MessagesResponse.final_action_type"],
				raw: phase4
					? {
							phase4,
							prompt_snapshot: phase4.summary.prompt_snapshot,
						}
					: {},
			},
		),
		event(
			response,
			11,
			"quality_completed",
			"reply_quality",
			"success",
			"质量检查完成",
			"基础质检已通过。",
			{
				output: phase5?.summary ?? {},
				field_refs: ["MessagesResponse.phase_trace.phase5_validate"],
				raw: phase5 ? { phase5 } : {},
			},
		),
		event(
			response,
			12,
			"trace_persisted",
			"audit",
			"success",
			"运行记录已保存",
			`trace=${response.trace_id}，session=${response.session_id}。`,
			{
				output: { trace_id: response.trace_id, session_id: response.session_id },
				field_refs: ["MessagesResponse.trace_id", "MessagesResponse.session_id"],
			},
		),
		event(
			response,
			13,
			"run_completed",
			"audit",
			response.final_outcome === "success" ? "success" : "warning",
			"本轮运行完成",
			`最终动作 ${response.final_action_type}，结果 ${response.final_outcome}。`,
			{
				output: {
					final_action_type: response.final_action_type,
					final_outcome: response.final_outcome,
				},
				field_refs: ["MessagesResponse.final_action_type", "MessagesResponse.final_outcome"],
			},
		),
	];
	return {
		run_id: runId,
		trace_id: response.trace_id,
		session_id: response.session_id,
		status: "completed",
		final_action_type: response.final_action_type,
		final_outcome: response.final_outcome,
		final_reply: response.reply_text,
		total_latency_ms: 18,
		events,
		messages_response: response,
	};
}

function stringifyFixtureValue(value: unknown): string {
	if (Array.isArray(value)) return value.length > 0 ? value.map(String).join(",") : "无";
	if (value === null || value === undefined || value === "") return "无";
	return String(value);
}

export function registerP2Fixtures(): void {
	registerFixture<PipelineIntentResponse>({
		pathname: "/api/v1/intent",
		methods: ["POST"],
		resolve: (ctx) => ({ delay_ms: 80, data: intentForMessage(requestMessage(ctx.body)) }),
	});

	registerFixture<PipelineDispatchResponse>({
		pathname: "/api/v1/dispatch",
		methods: ["POST"],
		resolve: (ctx) => ({
			delay_ms: 100,
			data: dispatchForIntent(intentForMessage(requestMessage(ctx.body))),
		}),
	});

	registerFixture<PipelineMessageResponse>({
		pathname: "/api/v1/messages",
		methods: ["POST"],
		resolve: (ctx) => ({
			delay_ms: 120,
			data: messageResponse(requestMessage(ctx.body)),
		}),
	});

	registerFixture<PipelineAdminRunResponse>({
		pathname: "/admin/v1/runs",
		methods: ["POST"],
		resolve: (ctx) => ({
			delay_ms: 160,
			data: adminRunResponse(requestMessage(ctx.body), ctx.body),
		}),
	});
}
